import {AppStateService} from "../services/appStateService";
import {ShapeInterface} from "../components/drawingComponent/interface/shapeInterface";
import cloneDeep from "lodash/cloneDeep";

export class ProductDataStore {
    private initialRelationships;
    private readonly initialAttributes;
    private readonly selectedOptions: {};
    private readonly attributes: {};
    private readonly availableOptions: {};
    private readonly productInfo: {};
    private readonly rules: {};
    private appStateService: AppStateService;
    private readonly productId: number;
    private readonly productType: string;
    private readonly sashConfiguration: any;
    private initialSashConfiguration: any;

    constructor() {
        this.appStateService = new AppStateService();
        const dataStore = this.appStateService.getProductDataStore();
        const initialDataStore = this.appStateService.getInitialProductDataStore();

        this.initialRelationships = cloneDeep(initialDataStore.data[0].relationships);
        this.initialAttributes = cloneDeep(initialDataStore.data[0].attributes);
        this.selectedOptions = dataStore.data[0].relationships;
        this.availableOptions = dataStore.included;
        this.attributes = dataStore.data[0].attributes;
        this.productInfo = dataStore.productInfo;
        this.rules = dataStore.rules;
        this.productId = dataStore.data[0].id;
        this.productType = dataStore.data[0].type;
        this.sashConfiguration = dataStore.data[0].attributes.sashConfiguration;
        this.initialSashConfiguration = cloneDeep(initialDataStore.data[0].attributes.sashConfiguration);
    }

    getSelectedOptionData(name)
    {
        const id = this.getSelectedOptionByName(name).id;
        return id === null ? id : this.getAvailableOptionByNameAndId(name, id);
    }

    setProductShapeDimension(dimensionName:string, dimensionValue): void
    {
        this.sashConfiguration.shapeDimensions[dimensionName] = parseInt(dimensionValue);
    }

    getProductShapeDimension(dimension:string): number
    {
        return this.sashConfiguration.shapeDimensions[dimension];
    }

    getProductShapeType(): string
    {
        return this.sashConfiguration.shapeType ?? '';
    }

    setProductShapeType(shapeType: string): void
    {
        this.sashConfiguration.shapeType = shapeType;
    }

    getProductShapeConfiguration(): ShapeInterface
    {
        return this.sashConfiguration.shapeDimensions;
    }

    getProductShape(): string
    {
        if (this.sashConfiguration) {
            return this.sashConfiguration.shape;
        }

        return '';
    }

    getAvailableOptionsByName(name): Array<any>
    {
        if (this.availableOptions[name] === undefined) {
            return [];
        }
        return this.availableOptions[name];
    }

    hasAvailableOptionsByName(name): boolean
    {
        if (this.availableOptions[name] === undefined) {
            return false;
        }

        return true;
    }

    getAvailableOptionByNameAndId(name: string, id: number, filterAvailableList: ((elem) => boolean) | undefined = undefined)
    {
        if (filterAvailableList === undefined) {
            filterAvailableList = (elem) => true;
        }
        const availableOptions = this.availableOptions[name].filter(filterAvailableList);

        if (availableOptions === undefined || availableOptions === null || availableOptions.length === 0) {
            console.error(`There is no available options for the key ${name}`);
            return null;
        }
        const selectedOptionData = availableOptions.find(option => option.id == id);

        if (selectedOptionData === undefined) {
            console.error(`Selected option id ${id} does not match available options ${name}`);
            //throw new Error(`Selected option id does not match available options`);
        }

        return selectedOptionData === undefined ? null : selectedOptionData;
    }

    getAttributeByName(name)
    {
        return this.attributes[name];
    }

    getSelectedOptionByName(name)
    {
        const selectedOption = this.selectedOptions[name];
        if (selectedOption === undefined) {
            throw new Error(`The key ${name} is not existing in the products option`);
        }

        return this.selectedOptions[name].data;
    }

    getInitialOptionByName(name)
    {
        return this.initialRelationships[name].data;
    }

    getInitialAttributeByName(name)
    {
        return this.initialAttributes[name];
    }

    getInitialSashConfiguration()
    {
        return this.initialSashConfiguration;
    }

    isSelected(name, id)
    {
        if (Array.isArray(this.selectedOptions[name].data)) {
            return this.selectedOptions[name].data.filter(function (option) {
                return option.id == id
            }).length !== 0;
        }

        return this.selectedOptions[name].data.id == id;
    }

    getSelectedOptionIdByName(name)
    {
        if (typeof this.selectedOptions[name] === 'undefined') {
            return null;
        }
        if (Array.isArray(this.selectedOptions[name].data)) {
            const ids = [];

            this.selectedOptions[name].data.forEach((option) => {
                ids.push(option.id);
            });

            return ids;
        }

        return this.selectedOptions[name].data.id;
    }

    getSelectedOption(selectedOptionName, availableOptionName) {
        const selectedOptionIds = this.getSelectedOptionIdByName(selectedOptionName);
        let selectedOption = null;

        if (!selectedOptionIds) {
            return selectedOption;
        }
        if (Array.isArray(selectedOptionIds)) {
            selectedOption = [];

            selectedOptionIds.forEach((selectedOptionId) => {
                const selectedOptionDetails = this.getAvailableOptionByNameAndId(
                    availableOptionName,
                    selectedOptionId,
                );
                if (selectedOptionDetails) {
                    selectedOption.push(selectedOptionDetails);
                }
            });
        } else {
            selectedOption = this.getAvailableOptionByNameAndId(
                availableOptionName,
                selectedOptionIds,
            )
        }
        return selectedOption;
    }

    getMoreInfoByModule(module: string)
    {
        const options = this.getAvailableOptionsByName('moreInfo');

        return options.find(option => option.attributes.field === module);
    }

    getProductInfoAttributeByName(name)
    {
        return this.productInfo[name];
    }

    updateSelectedOption(attributeName, attributeValue)
    {
        if (Array.isArray(this.selectedOptions[attributeName].data)) {
            this.selectedOptions[attributeName].data = attributeValue;
        } else {
            this.selectedOptions[attributeName].data.id = attributeValue;
        }
    }

    getData(): any
    {
        return {
            'data': [
                {
                    'id': this.productId,
                    'configurationId': this.initialRelationships.configuration.data.id,
                    'type': this.productType,
                    'relationships': this.selectedOptions,
                    'attributes': this.attributes,
                },
            ],
            'included': this.availableOptions,
            'productInfo': this.productInfo,
            'rules': this.rules
        };
    }

    getProductType()
    {
        return this.productType;
    }

    getSashConfiguration()
    {
        return this.sashConfiguration;
    }

    setAttributeByName(name, value)
    {
        return this.attributes[name] = value;
    }

    getProductId(): number {
        return this.productId;
    }

    getInsideColor()
    {
        const frameInnerColorId = this.getSelectedOptionByName('inner').id;
        return this.getAvailableOptionByNameAndId('color', frameInnerColorId);
    }

    getOutsideColor()
    {
        const frameOuterColorId = this.getSelectedOptionByName('outer').id;
        return this.getAvailableOptionByNameAndId('color', frameOuterColorId);
    }

    getMuntinInsideColor()
    {
        const muntinInnerColorId = this.getSelectedOptionByName('mullionColorInner').id;
        if (muntinInnerColorId === null) {
            return null;
        }
        return this.getAvailableOptionByNameAndId('color', muntinInnerColorId);
    }

    getMuntinOutsideColor()
    {
        const muntinOutsideColorId = this.getSelectedOptionByName('mullionColorOuter').id;
        if (muntinOutsideColorId === null) {
            return null;
        }
        return this.getAvailableOptionByNameAndId('color', muntinOutsideColorId);
    }

    public updateAccessoryPrices(positions) {
        positions.forEach((position) => {
            for (let i = 0; i < this.availableOptions['accessory'].length; i++) {
                if (position.id === this.availableOptions['accessory'][i].id) {
                    this.availableOptions['accessory'][i].attributes.price = position.attributes.price;
                    this.availableOptions['accessory'][i].attributes.priceAfterDiscount = position.attributes.priceAfterDiscount;
                    this.availableOptions['accessory'][i].attributes.netPrice = position.attributes.netPrice;
                    this.availableOptions['accessory'][i].attributes.netPriceAfterDiscount = position.attributes.netPriceAfterDiscount;
                    return true;
                }
            }
        });
        return this.availableOptions['accessory'];
    }

    getAllSelectedOptions() {
        return this.selectedOptions;
    }

    public updateAccessoryAvailability(availability) {
        Object.entries(availability).forEach((value) => {
            for (let i = 0; i < this.availableOptions['accessory'].length; i++) {
                if (Number(value[0]) === Number(this.availableOptions['accessory'][i].id)) {
                    this.availableOptions['accessory'][i].attributes.isBlocked = !value[1];
                    return true;
                }
            }
        });
    }

    public updatePacketsAvailability(availability) {
        Object.entries(availability).forEach((value) => {
            for (let i = 0; i < this.availableOptions['packet'].length; i++) {
                if (Number(value[0]) === Number(this.availableOptions['packet'][i].id)) {
                    this.availableOptions['packet'][i].attributes.isBlocked = !value[1];
                    return true;
                }
            }
        });
    }
}
